import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Package, Heart, MessageSquare, User as UserIcon, 
  Settings, Edit2, Trash2, Eye, Clock, MapPin, 
  Loader2, Bell, Lock, Shield, Plus, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'my-ads';
  const initialSection = searchParams.get('section') || '';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [user, setUser] = useState(null);
  const [myListings, setMyListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ full_name: '', phone: '', whatsapp: '' });
  const [settingsData, setSettingsData] = useState({
    emailNotifications: true, pushNotifications: false, smsNotifications: false,
    showPhone: true, showWhatsapp: true
  });

  const isAr = i18n.language === 'ar';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/auth'); return; }
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    setProfileData({ full_name: userData.full_name || '', phone: userData.phone || '', whatsapp: userData.whatsapp || '' });
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to section when navigating from menu
  useEffect(() => {
    if (initialSection && activeTab === 'settings') {
      setTimeout(() => {
        const el = document.getElementById(`section-${initialSection}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [initialSection, activeTab, loading]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [listingsRes, favoritesRes] = await Promise.all([
        axios.get(`${API}/users/me/listings`, config),
        axios.get(`${API}/favorites`, config)
      ]);
      setMyListings(listingsRes.data);
      setFavorites(favoritesRes.data);
    } catch (error) { console.error('Error fetching dashboard data:', error); }
    finally { setLoading(false); }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/users/me`, profileData, { headers: { Authorization: `Bearer ${token}` } });
      const updatedUser = res.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      alert(isAr ? 'تم تحديث الملف الشخصي!' : 'Profile updated successfully!');
    } catch (error) { alert(isAr ? 'خطأ في التحديث' : 'Error updating profile'); }
    finally { setSavingProfile(false); }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm(isAr ? 'هل أنت متأكد؟' : 'Are you sure you want to delete this listing?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/listings/${listingId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMyListings(myListings.filter(l => l.id !== listingId));
    } catch (error) { alert(isAr ? 'خطأ في الحذف' : 'Error deleting listing'); }
  };

  const formatPrice = (price) => new Intl.NumberFormat(isAr ? 'ar-AE' : 'en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(price);

  const stats = [
    { label: isAr ? 'إعلاناتي' : 'Total Ads', value: myListings.length, icon: Package, color: 'emerald' },
    { label: isAr ? 'المشاهدات' : 'Total Views', value: myListings.reduce((sum, l) => sum + (l.views_count || 0), 0), icon: Eye, color: 'blue' },
    { label: isAr ? 'المفضلة' : 'Favorites', value: favorites.length, icon: Heart, color: 'red' },
    { label: isAr ? 'الرسائل' : 'Messages', value: 0, icon: MessageSquare, color: 'purple' },
  ];

  const colorMap = {
    emerald: 'bg-emerald-500/15 text-emerald-400',
    blue: 'bg-blue-500/15 text-blue-400',
    red: 'bg-[#E31C25]/15 text-[#E31C25]',
    purple: 'bg-purple-500/15 text-purple-400'
  };

  const tabs = [
    { id: 'my-ads', label: t('dashboard.myAds'), icon: Package },
    { id: 'favorites', label: t('dashboard.favorites'), icon: Heart },
    { id: 'messages', label: t('dashboard.messages'), icon: MessageSquare },
  ];

  const inputClass = "w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-40 md:pb-8">
      {/* Hero Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-3xl mx-auto px-4 pt-4 pb-6 relative z-10">
          <div className="animate-fade-up">
            <p className="text-[#E31C25] text-xs font-bold uppercase tracking-widest mb-1.5">{isAr ? 'لوحة التحكم' : 'Dashboard'}</p>
            <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">
              {isAr ? `مرحباً, ${user?.full_name || ''}!` : `Welcome back, ${user?.full_name || 'User'}!`}
            </h1>
            <p className="text-white/40 text-sm mt-1">{isAr ? 'إدارة إعلاناتك وحسابك' : 'Manage your listings and account'}</p>
          </div>

          {/* Post Ad Button */}
          <button onClick={() => navigate('/post-ad')}
            className="w-full mt-4 py-3.5 bg-[#E31C25] text-white rounded-2xl font-bold text-sm hover:bg-[#C41820] transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98] flex items-center justify-center gap-2"
            data-testid="dashboard-post-ad">
            <Plus className="w-5 h-5" />
            {isAr ? 'نشر إعلان جديد' : 'Post New Ad'}
          </button>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-4">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className={`animate-scale-in stagger-${i + 1} bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4`}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2.5 ${colorMap[stat.color]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-2xl font-extrabold text-white">{stat.value}</p>
                  <p className="text-[10px] text-white/35 font-semibold uppercase tracking-wider mt-0.5">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto category-scroll pb-1 mb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id ? 'bg-[#E31C25] text-white shadow-lg shadow-[#E31C25]/25' : 'bg-white/8 text-white/50 hover:bg-white/12'
                }`} data-testid={`tab-${tab.id}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Profile & Settings Links */}
        <div className="grid grid-cols-2 gap-2.5 mb-5">
          <button onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-all ${activeTab === 'profile' ? 'bg-[#E31C25]/10 border-[#E31C25]/30' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`}
            data-testid="tab-profile">
            <div className="w-9 h-9 bg-white/[0.06] rounded-xl flex items-center justify-center">
              <UserIcon className={`w-4 h-4 ${activeTab === 'profile' ? 'text-[#E31C25]' : 'text-white/40'}`} />
            </div>
            <span className={`text-sm font-semibold ${activeTab === 'profile' ? 'text-white' : 'text-white/60'}`}>{t('menu.profile')}</span>
          </button>
          <button onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-all ${activeTab === 'settings' ? 'bg-[#E31C25]/10 border-[#E31C25]/30' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`}
            data-testid="tab-settings">
            <div className="w-9 h-9 bg-white/[0.06] rounded-xl flex items-center justify-center">
              <Settings className={`w-4 h-4 ${activeTab === 'settings' ? 'text-[#E31C25]' : 'text-white/40'}`} />
            </div>
            <span className={`text-sm font-semibold ${activeTab === 'settings' ? 'text-white' : 'text-white/60'}`}>{t('menu.accountSettings')}</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ===== MY ADS ===== */}
            {activeTab === 'my-ads' && (
              <div className="space-y-2.5">
                {myListings.length === 0 ? (
                  <div className="text-center py-16 animate-fade-up">
                    <div className="w-16 h-16 bg-white/[0.05] rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Package className="w-8 h-8 text-white/20" />
                    </div>
                    <p className="text-white font-bold text-sm mb-1">{isAr ? 'لا توجد إعلانات' : 'No ads posted yet'}</p>
                    <p className="text-white/30 text-xs mb-5">{isAr ? 'ابدأ بنشر إعلانك الأول' : 'Start by posting your first ad'}</p>
                    <button onClick={() => navigate('/post-ad')}
                      className="px-6 py-3 bg-[#E31C25] text-white rounded-xl text-sm font-bold shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]">
                      {isAr ? 'نشر إعلان' : 'Post Your First Ad'}
                    </button>
                  </div>
                ) : myListings.map((listing, i) => (
                  <div key={listing.id} className={`animate-fade-up stagger-${Math.min(i + 1, 9)} bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-3 flex gap-3`}
                    data-testid={`my-listing-${listing.id}`}>
                    <div className="w-24 h-24 bg-white/[0.06] rounded-xl overflow-hidden flex-shrink-0">
                      {listing.images?.[0] ? (
                        <img src={listing.images[0].image_url} alt={listing.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-white/15" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-bold text-white text-sm truncate">{listing.title}</h3>
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full shrink-0 ${
                          listing.status === 'active' ? 'bg-green-500/15 text-green-400' :
                          listing.status === 'pending' ? 'bg-amber-500/15 text-amber-400' :
                          'bg-white/10 text-white/50'
                        }`}>{listing.status}</span>
                      </div>
                      <p className="text-sm font-extrabold text-[#E31C25]">{formatPrice(listing.price)}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-white/30">
                        {listing.location?.city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{listing.location.city}</span>}
                        <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{listing.views_count}</span>
                        <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{new Date(listing.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => navigate(`/listing/${listing.id}`)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#E31C25] text-white rounded-lg text-[10px] font-bold active:scale-95">
                          <Eye className="w-3 h-3" />{isAr ? 'عرض' : 'View'}
                        </button>
                        <button onClick={() => navigate(`/edit-listing/${listing.id}`)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white/8 text-white/60 rounded-lg text-[10px] font-bold hover:bg-white/12 active:scale-95">
                          <Edit2 className="w-3 h-3" />{isAr ? 'تعديل' : 'Edit'}
                        </button>
                        <button onClick={() => handleDeleteListing(listing.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-[10px] font-bold hover:bg-red-500/20 active:scale-95">
                          <Trash2 className="w-3 h-3" />{isAr ? 'حذف' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ===== FAVORITES ===== */}
            {activeTab === 'favorites' && (
              <div className="space-y-2.5">
                {favorites.length === 0 ? (
                  <div className="text-center py-16 animate-fade-up">
                    <div className="w-16 h-16 bg-[#E31C25]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-[#E31C25]/40" />
                    </div>
                    <p className="text-white font-bold text-sm">{isAr ? 'لا توجد مفضلات' : 'No favorites yet'}</p>
                    <p className="text-white/30 text-xs mt-1">{isAr ? 'أضف إعلانات للمفضلة' : 'Save listings you like'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2.5">
                    {favorites.map((fav, i) => (
                      <div key={fav.id} onClick={() => navigate(`/listing/${fav.listing?.id}`)}
                        className={`animate-scale-in stagger-${Math.min(i + 1, 9)} bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] overflow-hidden cursor-pointer hover:border-[#E31C25]/30 transition-all active:scale-[0.98] group`}>
                        <div className="w-full aspect-[4/3] bg-white/[0.06] overflow-hidden">
                          {fav.listing?.images?.[0] ? (
                            <img src={fav.listing.images[0].image_url} alt={fav.listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-white/15" /></div>
                          )}
                        </div>
                        <div className="p-3">
                          <h3 className="font-bold text-white text-xs truncate mb-1">{fav.listing?.title}</h3>
                          <p className="text-sm font-extrabold text-[#E31C25]">{formatPrice(fav.listing?.price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ===== MESSAGES ===== */}
            {activeTab === 'messages' && (
              <div className="text-center py-16 animate-fade-up">
                <div className="w-16 h-16 bg-purple-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-purple-400/50" />
                </div>
                <p className="text-white font-bold text-sm mb-1">{isAr ? 'الرسائل' : 'Messages'}</p>
                <p className="text-white/30 text-xs">{isAr ? 'استخدم الدردشة للتواصل' : 'Use the chat feature to message sellers'}</p>
                <button onClick={() => navigate('/chats')}
                  className="mt-4 px-5 py-2.5 bg-white/8 text-white/60 rounded-xl text-sm font-bold hover:bg-white/12 transition-all active:scale-95 inline-flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />{isAr ? 'فتح الدردشة' : 'Open Chats'}
                </button>
              </div>
            )}

            {/* ===== PROFILE ===== */}
            {activeTab === 'profile' && (
              <div className="space-y-4 animate-fade-up">
                <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4">
                  <div className="flex items-center gap-2.5 mb-1">
                    <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
                      <UserIcon className="w-4 h-4 text-[#E31C25]" />
                    </div>
                    <h3 className="font-bold text-white text-sm">{t('menu.profile')}</h3>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('auth.fullName')}</label>
                    <input type="text" value={profileData.full_name}
                      onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      className={inputClass} data-testid="profile-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('auth.email')}</label>
                    <input type="email" value={user?.email || ''} disabled
                      className="w-full px-4 py-3.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white/30 text-sm cursor-not-allowed"
                      data-testid="profile-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('postAd.phone')}</label>
                    <input type="tel" value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className={inputClass} dir="ltr" data-testid="profile-phone" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('postAd.whatsapp')}</label>
                    <input type="tel" value={profileData.whatsapp}
                      onChange={(e) => setProfileData({ ...profileData, whatsapp: e.target.value })}
                      className={inputClass} dir="ltr" data-testid="profile-whatsapp" />
                  </div>
                  <button onClick={handleSaveProfile} disabled={savingProfile}
                    className="w-full py-3.5 bg-[#E31C25] text-white rounded-xl font-bold text-sm hover:bg-[#C41820] disabled:bg-white/10 disabled:text-white/30 transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98] flex items-center justify-center gap-2"
                    data-testid="profile-save">
                    {savingProfile ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>{t('common.saving')}</span></>) : t('common.save')}
                  </button>
                </div>
              </div>
            )}

            {/* ===== SETTINGS ===== */}
            {activeTab === 'settings' && (
              <div className="space-y-4 animate-fade-up">
                {/* Notifications */}
                <div id="section-notifications" className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
                      <Bell className="w-4 h-4 text-[#E31C25]" />
                    </div>
                    <h3 className="font-bold text-white text-sm">{t('menu.notificationSettings')}</h3>
                  </div>
                  <div className="space-y-3">
                    {[
                      { key: 'emailNotifications', label: t('settings.emailNotifications') },
                      { key: 'pushNotifications', label: t('settings.pushNotifications') },
                      { key: 'smsNotifications', label: t('settings.smsNotifications') },
                    ].map(item => (
                      <label key={item.key} className="flex items-center justify-between cursor-pointer py-1">
                        <span className="text-sm text-white/60">{item.label}</span>
                        <div className="relative" onClick={(e) => { e.preventDefault(); setSettingsData(s => ({ ...s, [item.key]: !s[item.key] })); }}>
                          <div className={`w-11 h-6 rounded-full transition-colors ${settingsData[item.key] ? 'bg-[#E31C25]' : 'bg-white/10'}`} />
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settingsData[item.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Privacy */}
                <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
                      <Shield className="w-4 h-4 text-[#E31C25]" />
                    </div>
                    <h3 className="font-bold text-white text-sm">{t('settings.privacy')}</h3>
                  </div>
                  <div className="space-y-3">
                    {[
                      { key: 'showPhone', label: t('settings.showPhone') },
                      { key: 'showWhatsapp', label: t('settings.showWhatsapp') },
                    ].map(item => (
                      <label key={item.key} className="flex items-center justify-between cursor-pointer py-1">
                        <span className="text-sm text-white/60">{item.label}</span>
                        <div className="relative" onClick={(e) => { e.preventDefault(); setSettingsData(s => ({ ...s, [item.key]: !s[item.key] })); }}>
                          <div className={`w-11 h-6 rounded-full transition-colors ${settingsData[item.key] ? 'bg-[#E31C25]' : 'bg-white/10'}`} />
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settingsData[item.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Security */}
                <div id="section-security" className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
                      <Lock className="w-4 h-4 text-[#E31C25]" />
                    </div>
                    <h3 className="font-bold text-white text-sm">{t('menu.security')}</h3>
                  </div>
                  <button className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white/60 text-sm font-semibold hover:bg-white/8 transition-all flex items-center justify-between">
                    <span>{t('settings.changePassword')}</span>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </button>
                </div>

                <button onClick={() => alert(t('common.saved'))}
                  className="w-full py-3.5 bg-[#E31C25] text-white rounded-xl font-bold text-sm hover:bg-[#C41820] transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]"
                  data-testid="settings-save">
                  {t('settings.saveSettings')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
